import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ExternalLink, ShieldCheck, ArrowRight, ArrowLeft, Radio } from "lucide-react";
import api from "@/lib/api";
import { Section, Overline, Reveal } from "@/components/Primitives";
import { Seo } from "@/components/Seo";
import { Breadcrumbs, breadcrumbSchema } from "@/components/Breadcrumbs";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "");
const FLAG = { canada: "🇨🇦", australie: "🇦🇺", global: "🌍" };
const TYPE_LABEL = { visa_update: "Mise à jour immigration", news: "Actualité", guide: "Guide" };

const SourceLine = ({ item }) => (
  <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
    <span className="inline-flex items-center gap-1.5">
      {item.official && <ShieldCheck size={13} className="text-gold" />} Source : <span className="text-navy">{item.source_name}</span>
    </span>
    <span>Publié le {fmt(item.published_at)}</span>
    <span>Mis à jour le {fmt(item.updated_at)}</span>
    {item.source_url && (
      <a href={item.source_url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1 text-gold hover:underline">
        Source officielle <ExternalLink size={12} />
      </a>
    )}
  </div>
);

export default function WteLive() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/live/items", { params: { country: filter || undefined, limit: 60 } })
      .then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [filter]);

  return (
    <div data-testid="wte-live-page">
      <Seo
        title="WTE LIVE — actualités, mises à jour visa et informations Australie & Canada | Work & Travel Expert"
        description="WTE LIVE : les mises à jour officielles d'immigration (IRCC, Department of Home Affairs), les actualités, la météo et les informations utiles aux candidats vers l'Australie et le Canada. Chaque information est sourcée et datée."
        keywords="actualité immigration canada, mise à jour visa australie, news IRCC, actualités immigration francophone, wte live"
        path="/wte-live"
        schema={[breadcrumbSchema([{ name: "WTE LIVE", to: "/wte-live" }])]}
      />

      <div className="relative bg-navy">
        <Section className="py-20">
          <Reveal>
            <Breadcrumbs theme="dark" items={[{ name: "WTE LIVE" }]} />
            <div className="mt-5" />
            <Overline>Chaîne d'information</Overline>
            <h1 className="mt-4 max-w-3xl text-4xl font-medium leading-[1.08] text-white sm:text-5xl">
              WTE <span className="text-gold">LIVE</span> — l'info immigration Australie &amp; Canada
            </h1>
            <div className="gold-line mt-6" />
            <p className="mt-6 max-w-2xl leading-relaxed text-white/80">
              Mises à jour officielles, actualités, météo et informations utiles aux candidats. Chaque information affiche
              sa source, sa date de publication et sa date de mise à jour, avec un lien vers la source officielle lorsque disponible.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {[["", "Tout"], ["canada", "🇨🇦 Canada"], ["australie", "🇦🇺 Australie"]].map(([v, l]) => (
                <button key={v || "all"} data-testid={`live-filter-${v || "all"}`} onClick={() => setFilter(v)}
                  className={`rounded-full border px-4 py-2 text-xs uppercase tracking-[0.14em] transition-colors ${
                    filter === v ? "border-gold bg-gold text-navy" : "border-white/20 text-white/75 hover:border-gold hover:text-gold"}`}>
                  {l}
                </button>
              ))}
            </div>
          </Reveal>
        </Section>
      </div>

      <Section className="py-16">
        {items.length === 0 && <p className="text-sm text-muted-foreground">Aucune information disponible pour le moment.</p>}
        <div className="grid gap-6 md:grid-cols-2">
          {items.map((i) => (
            <article key={i.slug} data-testid={`live-card-${i.slug}`}
              className="flex flex-col border border-border bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-xl">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-gold">
                {i.official ? <ShieldCheck size={13} /> : <Radio size={13} />} {FLAG[i.country]} {TYPE_LABEL[i.type] || i.type}
              </div>
              <h2 className="mt-3 font-heading text-xl leading-snug text-navy">
                <Link to={`/wte-live/${i.slug}`} className="transition-colors hover:text-gold">{i.title}</Link>
              </h2>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-700">{i.summary}</p>
              <SourceLine item={i} />
              <Link to={`/wte-live/${i.slug}`} className="group mt-5 inline-flex items-center gap-1.5 text-sm text-navy transition-colors hover:text-gold">
                Lire l'information <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </article>
          ))}
        </div>
      </Section>
    </div>
  );
}

export function WteLiveItem() {
  const { slug } = useParams();
  const [item, setItem] = useState(undefined);

  useEffect(() => {
    api.get(`/live/items/${slug}`).then((r) => setItem(r.data)).catch(() => setItem(null));
  }, [slug]);

  if (item === null) return <Navigate to="/wte-live" replace />;
  if (!item) return <Section className="py-24"><p className="text-sm text-muted-foreground">Chargement…</p></Section>;

  return (
    <div data-testid={`wte-live-item-${slug}`}>
      <Seo
        title={`${item.title} | WTE LIVE — Work & Travel Expert`}
        description={item.summary?.slice(0, 300)}
        path={`/wte-live/${slug}`}
        type="article"
        schema={[
          breadcrumbSchema([{ name: "WTE LIVE", to: "/wte-live" }, { name: item.title, to: `/wte-live/${slug}` }]),
          {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: item.title,
            description: item.summary,
            datePublished: item.published_at,
            dateModified: item.updated_at,
            inLanguage: "fr-FR",
            isBasedOn: item.source_url || undefined,
            author: { "@type": "Organization", name: item.source_name || "Work & Travel Expert" },
            publisher: { "@type": "Organization", name: "Work & Travel Expert", url: "https://worktravelexpert.net" },
            mainEntityOfPage: `https://worktravelexpert.net/wte-live/${slug}`,
          },
        ]}
      />

      <div className="bg-navy">
        <Section className="py-16">
          <Breadcrumbs theme="dark" items={[{ name: "WTE LIVE", to: "/wte-live" }, { name: item.title }]} />
          <span className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-gold">
            {item.official ? <ShieldCheck size={13} /> : <Radio size={13} />} {FLAG[item.country]} {TYPE_LABEL[item.type] || item.type}
          </span>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl font-medium leading-tight text-white sm:text-4xl">{item.title}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-white/65">
            <span>Source : <span className="text-white/90">{item.source_name}</span></span>
            <span>Publié le {fmt(item.published_at)}</span>
            <span>Mis à jour le {fmt(item.updated_at)}</span>
          </div>
        </Section>
      </div>

      <Section className="max-w-3xl py-16">
        <p className="text-base leading-relaxed text-slate-700">{item.summary}</p>
        {item.body && <div className="mt-6 whitespace-pre-wrap text-base leading-relaxed text-slate-700">{item.body}</div>}

        {item.source_url && (
          <a href={item.source_url} target="_blank" rel="noopener noreferrer nofollow" data-testid="live-item-source"
            className="mt-8 inline-flex items-center gap-2 border border-navy/20 px-6 py-3.5 text-sm font-medium text-navy transition-colors hover:border-gold hover:text-gold">
            Consulter la source officielle <ExternalLink size={15} />
          </a>
        )}

        <div className="mt-12 bg-navy p-8 text-white sm:p-10">
          <h2 className="font-heading text-2xl sm:text-3xl">Cette information concerne votre projet ?</h2>
          <p className="mt-4 leading-relaxed text-white/75">
            Faites le point avec un conseiller lors d'une consultation gratuite de 30 minutes, ou posez vos questions à WTE AI, disponible 24h/24.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/rendez-vous" data-testid="live-item-cta" className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-xs font-bold uppercase tracking-[0.1em] text-navy transition-colors hover:bg-gold-hover">
              Consultation gratuite <ArrowRight size={15} />
            </Link>
            <Link to="/nos-packs" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-xs font-bold uppercase tracking-[0.1em] text-white transition-colors hover:border-gold hover:text-gold">
              Voir nos packs
            </Link>
          </div>
        </div>

        <Link to="/wte-live" className="mt-10 inline-flex items-center gap-2 text-sm text-navy transition-colors hover:text-gold">
          <ArrowLeft size={15} /> Retour à WTE LIVE
        </Link>
      </Section>
    </div>
  );
}
